import {
    BehaviorSubject,
    Connectable,
    Observable,
    Subscription,
    combineLatest,
    connectable,
    fromEvent,
    merge,
    of,
} from "rxjs";
import { filter, map, skip, startWith, switchMap } from "rxjs/operators";

import {
    LOADEDMETADATA_EVENT,
    LOADING_STATUS,
    PAUSE_EVENT,
    PAUSE_STATUS,
    PLAYING_EVENT,
    PLAYING_STATUS,
    PLAY_EVENT,
    PROGRESS_EVENT,
    SEEKED_EVENT,
    TIMEUPDATE_EVENT,
    VOLUMECHANGE_EVENT,
    WAITING_EVENT,
} from "./constants";
import { VideoControllerStatusType } from "./types";
import { secondsToFormatedTime } from "./utils";

export class VideoController {
    public status$: Connectable<VideoControllerStatusType>;
    public currentStatus$ = new BehaviorSubject<VideoControllerStatusType>(
        PAUSE_STATUS
    );
    public time$: Connectable<number>;
    public progress$: Connectable<number>;
    public formatedTime$: Observable<string>;
    public duration$: Connectable<number>;
    public formatedDuration$: Observable<string>;
    public volume$: Connectable<number>;
    public currentVolume$ = new BehaviorSubject<number>(0);
    public buffered$: Connectable<number>;
    public fullscreen$ = new BehaviorSubject(false);
    private subscriptions: Subscription[] = [];
    private videoElement: HTMLVideoElement | null = null;

    constructor() {
        // status
        this.status$ = connectable(
            of(0).pipe(
                switchMap(() => {
                    if (!this.videoElement) throw new Error();
                    return merge(
                        fromEvent(this.videoElement, PLAY_EVENT),
                        fromEvent(this.videoElement, PLAYING_EVENT),
                        fromEvent(this.videoElement, PAUSE_EVENT),
                        fromEvent(this.videoElement, WAITING_EVENT)
                    );
                }),
                map((v) => {
                    switch (v.type) {
                        case PLAY_EVENT:
                            return PLAYING_STATUS;
                        case PLAYING_EVENT:
                            return PLAYING_STATUS;
                        case PAUSE_EVENT:
                            return PAUSE_STATUS;
                        case WAITING_EVENT:
                            return LOADING_STATUS;
                        default:
                            return PAUSE_STATUS;
                    }
                })
            ),
            {
                connector: () =>
                    new BehaviorSubject<VideoControllerStatusType>(
                        PAUSE_STATUS
                    ),
            }
        );
        this.status$.subscribe(this.currentStatus$);
        // time
        this.time$ = connectable(
            of(0).pipe(
                switchMap(() => {
                    if (!this.videoElement) throw new Error();
                    return merge(
                        fromEvent(this.videoElement, TIMEUPDATE_EVENT),
                        fromEvent(this.videoElement, SEEKED_EVENT)
                    );
                }),
                map(() => {
                    if (!this.videoElement) throw new Error();
                    return this.videoElement.currentTime;
                })
            ),
            {
                connector: () => new BehaviorSubject(0),
            }
        );
        this.formatedTime$ = this.time$.pipe(
            map((v) => secondsToFormatedTime(v))
        );
        // volume
        this.volume$ = connectable(
            of(0).pipe(
                switchMap(() => {
                    if (!this.videoElement) throw new Error();
                    return merge(
                        fromEvent(this.videoElement, VOLUMECHANGE_EVENT)
                    );
                }),
                map(() => {
                    if (!this.videoElement) throw new Error();
                    return this.videoElement.volume;
                }),
                (o: Observable<number>): Observable<number> => {
                    return new Observable((subscriber) => {
                        if (!this.videoElement) throw new Error();
                        subscriber.next(this.videoElement.volume);
                        return o.subscribe(subscriber);
                    });
                }
            ),
            {
                connector: () => new BehaviorSubject(0),
            }
        );
        this.volume$.subscribe(this.currentVolume$);
        // duration
        this.duration$ = connectable(
            of(0).pipe(
                switchMap(() => {
                    if (!this.videoElement) throw new Error();
                    return merge(
                        fromEvent(this.videoElement, LOADEDMETADATA_EVENT),
                        fromEvent(this.videoElement, TIMEUPDATE_EVENT)
                    );
                }),
                map(() => {
                    if (!this.videoElement) throw new Error();
                    return this.videoElement.duration;
                })
            ),
            {
                connector: () => new BehaviorSubject(0),
            }
        );
        this.formatedDuration$ = this.duration$.pipe(
            map((v) => secondsToFormatedTime(v))
        );
        // buffered
        this.buffered$ = connectable(
            of(0).pipe(
                switchMap(() => {
                    if (!this.videoElement) throw new Error();
                    return merge(fromEvent(this.videoElement, PROGRESS_EVENT));
                }),
                map(() => {
                    if (!this.videoElement) throw new Error();
                    if (!this.videoElement.buffered.length) return 0;
                    return this.videoElement.buffered.end(
                        this.videoElement.buffered.length - 1
                    );
                })
            ),
            {
                connector: () => new BehaviorSubject(0),
            }
        );
        this.progress$ = connectable(
            of(0).pipe(
                switchMap(() => {
                    return combineLatest([
                        this.time$.pipe(startWith(0)),
                        this.duration$.pipe(skip(1)),
                    ]);
                }),
                filter(([_, duration]) => duration > 0),
                map(([time, duration]) => {
                    return (time * 100) / duration;
                })
            ),
            {
                connector: () => new BehaviorSubject(0),
            }
        );
    }

    connect(videoElement: HTMLVideoElement): void {
        this.videoElement = videoElement;
        this.subscriptions = [
            this.time$.connect(),
            this.status$.connect(),
            this.volume$.connect(),
            this.duration$.connect(),
            this.buffered$.connect(),
            this.progress$.connect(),
        ];
    }

    play(): void {
        void this.videoElement?.play();
    }

    pause(): void {
        this.videoElement?.pause();
    }

    seek(time: number): void {
        if (!this.videoElement) throw new Error();
        this.videoElement.currentTime = time;
    }

    changeVolume(volume: number): void {
        if (!this.videoElement) throw new Error();
        this.videoElement.volume = volume;
    }

    enterFullscreen(): void {
        this.fullscreen$.next(true);
    }

    leaveFullscreen(): void {
        this.fullscreen$.next(false);
    }

    destroy(): void {
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
    }
}
